import {CacheService, CustomerService, IApplicationFilter, property, widget, WidgetPropertyType} from '@dynamik/common';
import {BindingEngine, computedFrom} from 'aurelia-binding';
import {WidgetBase} from '@dynamik/common';
import {customElement} from 'aurelia-templating';
import {CategoriesClient} from '@dynamik/common/clients/transaction-clients';
import {InstrumentCostBreakdownViewModel, PortfolioReportClient} from '@dynamik/common/clients/reporting-clients';
import {DisplayedFields} from '../displayed-fields';
import {CategoryType} from '../../../classes/category-type';

declare module '@dynamik/common/clients/reporting-clients' {
    interface InstrumentCostBreakdownViewModel {
        groupTotal?: boolean;
        isHeader?: boolean;
        isChild?: boolean;
        isGroupTotal?: boolean;
        hasChildren?: boolean;
    }
}

@customElement('cost-breakdown-widget')
@widget('Cost Breakdown', true, false, 'Description')
export class CostBreakdownWidget extends WidgetBase {
    constructor(element: Element, customerService: CustomerService, private portfolioReportClient: PortfolioReportClient,
                bindingEngine: BindingEngine, private cacheService: CacheService, private categoriesClient: CategoriesClient) {
        super(element, bindingEngine, customerService);
    }

    allData: InstrumentCostBreakdownViewModel[];
    data: InstrumentCostBreakdownViewModel[];
    currentPage = 0;
    lastItemRendered: (value: unknown) => void;
    DisplayedFields = DisplayedFields;
    currentFilter: IApplicationFilter | null = null;

    // Template management
    templateName = 'CostBreakdown'; // Widget name with spaces trimmed
    apiBaseUrl = 'http://localhost:5410';

    @property('Data', 'Portfolio', WidgetPropertyType.List)
    portfolio?: string;

    async portfolioListValues() {
        return [
            {value: undefined, label: 'All portfolios'},
            ...(await this.categoriesClient.getSecCategories(this.customerService.selectedCustomer.companyId, CategoryType.Portfolio))
                .map(x => ({value: x.name, label: x.name}))
        ];
    }

    portfolioChanged(newValue: string, oldValue: string) {
        if (!this.title) {
            return;
        }

        this.title = this.title.replace(` (${oldValue})`, '') + (newValue ? ` (${newValue})` : '');
    }

    @property('Data', undefined, WidgetPropertyType.List, [{value: 'lastTransactionDate', label: 'Date'}, {value: 'name', label: 'Name'}])
    sortBy: 'lastTransactionDate' | 'name' = 'lastTransactionDate';

    @property('Data')
    descending: boolean = true;

    @property('Data')
    quantityPrecision?: number = 0;

    @property('Data')
    precision: number = 2;

    @property('Table')
    font?: string;

    @property('Table')
    fontSize?: number = 11;

    @property('Table')
    color?: string;

    @property('Table')
    rowHeight?: number;

    @property('Table')
    rowsPerPage: number = 1000;

    @property('Table')
    columnWidths?: string = '200,110';

    @property()
    captionFont?: string;

    @property()
    captionFontSize?: number = 11;

    @property()
    captionColor?: string;

    @property('Data', 'Displayed fields', WidgetPropertyType.List)
    displayedFields?: DisplayedFields;

    async displayedFieldsListValues() {
        return [
            {value: undefined, label: 'Default'},
            {value: DisplayedFields.All, label: 'All fields'},
            {value: DisplayedFields.Fifo, label: 'FIFO'}
        ];
    }

    @computedFrom('displayedFields', 'customerService.selectedCustomer.useAverageCostPrice')
    get displayAvg() {
        return this.customerService.selectedCustomer.useAverageCostPrice && [undefined, DisplayedFields.All].includes(this.displayedFields);
    }

    @computedFrom('displayedFields', 'customerService.selectedCustomer.useAverageCostPrice')
    get displayFifo() {
        return !this.customerService.selectedCustomer.useAverageCostPrice || [DisplayedFields.Fifo, DisplayedFields.All].includes(this.displayedFields);
    }

    protected async loadData(filter: IApplicationFilter) {
        this.currentFilter = filter;

        return await this.cacheService.getOrSet(`processed-vouchers-currency-widget-${filter.customer.companyId}-${this.portfolio}-${filter.dates.fromStr}-${filter.dates.toStr}`,
            () => this.portfolioReportClient.costBreakdown(filter.customer.companyId, filter.dates.fromStr, filter.dates.toStr, true, this.portfolio), 60000, false);
    }

    protected async setData(data: InstrumentCostBreakdownViewModel[]) {
        const groups = new Map<string, InstrumentCostBreakdownViewModel[]>();
        for (const item of data) {
            const key = item.instrumentId ? item.instrumentId.toString() : `${item.name}_${item.isin || item.orgNr}`;
            if (!groups.has(key)) {
                groups.set(key, []);
            }
            groups.get(key)!.push(item);
        }

        const flattenedData: InstrumentCostBreakdownViewModel[] = [];
        const topLevelGroups: { header: InstrumentCostBreakdownViewModel, items: InstrumentCostBreakdownViewModel[], groupTotal?: InstrumentCostBreakdownViewModel }[] = [];

        for (const group of groups.values()) {
            if (group.length === 1) {
                const row = new InstrumentCostBreakdownViewModel(group[0]);
                row.isHeader = true;
                row.hasChildren = false;
                topLevelGroups.push({header: row, items: []});
            } else {
                const header = new InstrumentCostBreakdownViewModel();
                header.isHeader = true;
                header.hasChildren = true;
                header.name = group[0].name;
                header.isin = group[0].isin;
                header.orgNr = group[0].orgNr;
                header.instrumentId = group[0].instrumentId;
                header.currencyCode = '';

                const children = group.map(c => {
                    const child = new InstrumentCostBreakdownViewModel(c);
                    child.isChild = true;
                    child.name = '';
                    return child;
                });

                const gTotal = new InstrumentCostBreakdownViewModel();
                gTotal.isGroupTotal = true;
                gTotal.name = `Total ${group[0].name}`;
                gTotal.quantity = group.reduce((sum, item) => sum + (item.quantity || 0), 0);
                gTotal.totalCostPrice = group.reduce((sum, item) => sum + (item.totalCostPrice || 0), 0);
                gTotal.totalCostPriceInCurrency = group.reduce((sum, item) => sum + (item.totalCostPriceInCurrency || 0), 0);
                gTotal.totalCostPriceInPeriod = group.reduce((sum, item) => sum + (item.totalCostPriceInPeriod || 0), 0);
                gTotal.totalCostPriceInCurrencyInPeriod = group.reduce((sum, item) => sum + (item.totalCostPriceInCurrencyInPeriod || 0), 0);
                gTotal.avgTotalCostPrice = group.reduce((sum, item) => sum + (item.avgTotalCostPrice || 0), 0);
                gTotal.avgTotalCostPriceInCurrency = group.reduce((sum, item) => sum + (item.avgTotalCostPriceInCurrency || 0), 0);
                gTotal.avgTotalCostPriceInPeriod = group.reduce((sum, item) => sum + (item.avgTotalCostPriceInPeriod || 0), 0);
                gTotal.avgTotalCostPriceInCurrencyInPerio = group.reduce((sum, item) => sum + (item.avgTotalCostPriceInCurrencyInPerio || 0), 0);

                topLevelGroups.push({header, items: children, groupTotal: gTotal});
            }
        }

        const sortedGroups = topLevelGroups.sort((a, b) => {
            // @ts-ignore
            const valA = a.header[this.sortBy];
            // @ts-ignore
            const valB = b.header[this.sortBy];

            if (valA === valB) return 0;
            if (valA === undefined || valA === null) return this.descending ? 1 : -1;
            if (valB === undefined || valB === null) return this.descending ? -1 : 1;

            const result = valA > valB ? 1 : -1;
            return this.descending ? result * -1 : result;
        });

        for (const group of sortedGroups) {
            flattenedData.push(group.header);
            if (group.items.length > 0) {
                flattenedData.push(...group.items);
                if (group.groupTotal) {
                    flattenedData.push(group.groupTotal);
                }
            }
        }

        const grandTotal = new InstrumentCostBreakdownViewModel();
        grandTotal.groupTotal = true;
        grandTotal.name = 'Grand Total';
        grandTotal.quantity = undefined;
        grandTotal.totalCostPrice = data.reduce((sum, item) => sum + (item.totalCostPrice || 0), 0);
        grandTotal.totalCostPriceInCurrency = data.reduce((sum, item) => sum + (item.totalCostPriceInCurrency || 0), 0);
        grandTotal.totalCostPriceInPeriod = data.reduce((sum, item) => sum + (item.totalCostPriceInPeriod || 0), 0);
        grandTotal.totalCostPriceInCurrencyInPeriod = data.reduce((sum, item) => sum + (item.totalCostPriceInCurrencyInPeriod || 0), 0);
        grandTotal.avgTotalCostPrice = data.reduce((sum, item) => sum + (item.avgTotalCostPrice || 0), 0);
        grandTotal.avgTotalCostPriceInCurrency = data.reduce((sum, item) => sum + (item.avgTotalCostPriceInCurrency || 0), 0);
        grandTotal.avgTotalCostPriceInPeriod = data.reduce((sum, item) => sum + (item.avgTotalCostPriceInPeriod || 0), 0);
        grandTotal.avgTotalCostPriceInCurrencyInPerio = data.reduce((sum, item) => sum + (item.avgTotalCostPriceInCurrencyInPerio || 0), 0);

        this.allData = [...flattenedData, grandTotal];
        this.currentPage = 0;
        this.updatePageData();

        // Populate excelData for right-click export
        this.excelData = {
            fileName: 'CostBreakdown',
            data: {
                Items: data.map(item => ({
                    Name: item.name || '',
                    ISIN: item.isin || '',
                    'Org Nr': item.orgNr || '',
                    Currency: item.currencyCode || '',
                    Quantity: item.quantity || 0,
                    'Currency Amount': item.totalCostPriceInCurrency || 0,
                    Amount: item.totalCostPrice || 0,
                    'Amount In Period': item.totalCostPriceInPeriod || 0
                }))
            }
        };
    }

    private updatePageData() {
        this.data = this.allData
            .filter((x, i) => this.currentPage * this.rowsPerPage <= i && i < (this.currentPage + 1) * this.rowsPerPage);
    }

    getPageCount(): number {
        return this.allData ? Math.ceil(this.allData.length / this.rowsPerPage) : 0;
    }

    async setPage(page: number): Promise<void> {
        if (page === this.currentPage) {
            return;
        }
        this.currentPage = page;
        this.updatePageData();
        await new Promise(resolve => this.lastItemRendered = resolve);
    }


    /**
     * Export the cost breakdown data to Excel using template injection
     * Populates the template's table with actual data
     */

    // @ts-ignore
    override configChanged(key: keyof ProcessedVouchersCurrencyWidget, newValue: any, oldValue: any) {
        switch (key) {
            case 'rowsPerPage':
                this.updatePageData();
                break;
            case 'portfolio':
                this.portfolioChanged(newValue, oldValue);
                break;
            case 'quantityPrecision':
            case 'precision':
            case 'sortBy':
            case 'descending':
                this.refresh$!.next();
                break;
            default:
                break;
        }
    }

}
